// Edit record component displays the modal content for updating a record.
import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { stringify } from 'qs';
import {
  Input,
  Form,
  message,
  Tag,
  Alert,
  Checkbox,
  Row,
  Col,
  Tooltip,
  Select,
  Button,
} from 'antd';

import { captureAnalytics } from '../../actions/preload';
import DatePicker from '../DatePicker';
import ModalWrapper from './ModalWrapper';
import PrivacyLevel from './PrivacyLevel';
import { formatDate, validDOB, validPhoneNumber } from '../../global/validation';
import { verifyFeatureAccess } from '../../global/featureFlags';
import dedupeDocumentCategories from '../../utils/dedupeDocumentCategories';
import { makeSingleFlight } from '../../utils/singleFlight';
import {
  forgetPrivacyLevel,
  printerUploadPromptEnabled,
  printerUploadPromptTitle,
  rememberedPrivacyLevel,
  rememberPrivacyLevel,
} from '../../util/printerDriverUpload';
import {
  buildSparsePatientChanges,
  isValidPatientDOB,
  normalizePatientDraft,
} from '../../utils/emailAttachmentPatientUpdate';
import {
  referralCountDelta,
  sparsePatientSaveEligible,
} from './emailAttachmentPatientSave';
import './editPatient.scss';

const { Search } = Input;
const { Option } = Select;

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  sort: (a, b) => (a.localeCompare(b)),
};

/* global toastr */

// The typing timer is used in the search bar. It waits until the user's
// keystrokes are idle for 1 second before sending the request
// to Rails
let typingTimer = null;

const IDLE_SPARSE_PATIENT_UPDATE = {
  fileToken: null,
  isLoading: false,
  errorMessage: null,
};

export class EditPatient extends Component {
  formRef = React.createRef();

  static propTypes = {
    hideModal: PropTypes.func.isRequired,
    modalType: PropTypes.object.isRequired,
    sendToken: PropTypes.string,
    allDocumentCategories: PropTypes.object.isRequired,
    getDocumentCategories: PropTypes.func.isRequired,
    edit: PropTypes.func.isRequired,
    rowSelected: PropTypes.func.isRequired,
    mode: PropTypes.string.isRequired,
    isLoading: PropTypes.bool.isRequired,
    editReceivedRecordLabels: PropTypes.func.isRequired,
    data: PropTypes.array.isRequired,
    getLabelsByQuery: PropTypes.func.isRequired,
    selectedRecord: PropTypes.array.isRequired,
    incrementReferralCount: PropTypes.func.isRequired,
    decrementReferralCount: PropTypes.func.isRequired,
    sparseEdit: PropTypes.func.isRequired,
    sparsePatientUpdate: PropTypes.shape({
      fileToken: PropTypes.string,
      isLoading: PropTypes.bool.isRequired,
      errorMessage: PropTypes.string,
    }),
    reportSparseUpdateError: PropTypes.func.isRequired,
    updateSelectedRecordLabels: PropTypes.func.isRequired,
    updatedLabel: PropTypes.bool,
  }

  static defaultProps = {
    sendToken: '',
    sparsePatientUpdate: IDLE_SPARSE_PATIENT_UPDATE,
    updatedLabel: false,
  }

  editorActive = true;

  openingPatientBaseline = null;

  sparseSaveEnabled = sparsePatientSaveEligible(this.props.modalType.data);

  update = makeSingleFlight((event) => this.performUpdate(event));

  state = {
    patientName: this.props.modalType.data.patient.patient_name,
    patientFirstName: '',
    patientMiddleName: '',
    patientLastName: '',
    patientNameChanged: false,
    patientDOB: this.props.modalType.data.patient.patient_dob,
    patientPhoneNumber: this.props.modalType.data.patient.patient_phone_number || '',
    fileToken: this.props.modalType.data.patient.file_token,
    nameValid: '',
    patientValid: '',
    // The record's own level, always: a Printer Driver arrival's remembered
    // level is applied later, once department_sharing says the picker will be
    // visible (see rememberedPrivacyLevelUpdate).
    privacyLevel: this.props.modalType.data.patient.privacy_level,
    // Mirrors UploadSingleRecord: checked by default, so a user who picks a
    // level once stops being asked to pick it again.
    useSelectedPrivacyLevel: true,
    hideOnlyMePrivacyLevel: false,
    labels: [],
    selectedLabels: [],
    searchLoading: false,
    sendTokens: this.props.modalType.data.patient.send_token,
    autoLabelAccess: true,
    isPatientInfoIncorrect: false,
    incorrectDOB: false,
    incorrectName: false,
    initialLabels: [],
    documentTitle: this.props.modalType.data.patient.document_title,
    documentCategories: [],
    documentCategory: this.props.modalType.data.patient.document_category,
    referralNote: this.props.modalType.data.patient.referral_note,
    referringProvider: this.props.modalType.data.patient.referring_provider,
    referringProviderOffice: this.props.modalType.data.patient.provider_office,
    display: false,
  };

  componentWillMount = () => {
    const { modalType } = this.props;
    let firstName = modalType.data.patient.patient_first_name || '';
    let lastName = modalType.data.patient.patient_last_name || '';
    let middleName = modalType.data.patient.patient_middle_name || '';

    if (!firstName || !lastName) {
      // Parse the existing patient name into components
      const fullName = modalType.data.patient.patient_name || '';
      const nameParts = fullName.trim().split(' ').filter((part) => part.length > 0);

      if (nameParts.length === 1) {
        firstName = nameParts[0];
      } else if (nameParts.length === 2) {
        firstName = nameParts[0];
        lastName = nameParts[1];
      } else if (nameParts.length >= 3) {
        firstName = nameParts[0];
        lastName = nameParts[nameParts.length - 1];
        middleName = nameParts.slice(1, -1).join(' ');
      }
    }

    const patientName = middleName ? `${firstName} ${middleName} ${lastName}` : `${firstName} ${lastName}`;
    this.openingPatientBaseline = Object.freeze(normalizePatientDraft({
      patientFirstName: firstName,
      patientMiddleName: middleName,
      patientLastName: lastName,
      patientDOB: modalType.data.patient.patient_dob,
      patientPhoneNumber: modalType.data.patient.patient_phone_number,
      documentTitle: modalType.data.patient.document_title,
      documentCategory: modalType.data.patient.document_category,
      privacyLevel: modalType.data.patient.privacy_level,
      referralNote: modalType.data.patient.referral_note,
      referringProvider: modalType.data.patient.referring_provider,
      referringProviderOffice: modalType.data.patient.provider_office,
    }));
    this.setState({
      patientFirstName: firstName,
      patientLastName: lastName,
      patientMiddleName: middleName,
      patientName: patientName.trim(),
    })
  }

  componentDidMount = async () => {
    this.editorActive = true;
    // eslint-disable-next-line react/destructuring-assignment
    const page = 1;
    const pageSize = 20;
    const { getLabelsByPage, getDocumentCategories } = this.props;
    captureAnalytics('edit_record_info_opened', {
      from_page: this.props.modalType.data.fromPage || 'unknown',
      privacy_only: this.privacyOnlyMode(),
    });
    if (!this.privacyOnlyMode()) {
      await getLabelsByPage(page, pageSize);
      await getDocumentCategories();
    }
    const { data, selectedRecord, modalType } = this.props;
    const autoLabel = this.privacyOnlyMode() ? true : await verifyFeatureAccess('ai_extraction');
    this.setState(() => {
      const labelsState = modalType.data.selectedLabelsRecordViewer ? modalType.data.selectedLabelsRecordViewer : selectedRecord[0]?.labels || [];
      return {
        labels: data,
        selectedLabels: [...labelsState],
        initialLabels: [...labelsState],
        privacyLevel: this.props.modalType.data.patient.privacy_level,
        autoLabelAccess: autoLabel,
        documentCategories: this.privacyOnlyMode() ? [] : this.props.allDocumentCategories.categories,
      };
    });
    this.determineVisibility();
    this.validateFormFields();
  }

  componentWillReceiveProps(nextProps) {
    const {
      patientDOB,
      patientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      patientPhoneNumber,
      privacyLevel,
      documentTitle,
      documentCategory,
      fileToken: currentFileToken,
    } = this.state;

    const nextPatient = nextProps.modalType.data.patient;
    const newPatientName = nextPatient.patient_name;
    const newPatientFirstName = nextPatient.patient_first_name;
    const newPatientMiddleName = nextPatient.patient_middle_name;
    const newPatientLastName = nextPatient.patient_last_name;
    const newPatientDOB = nextPatient.patient_dob;
    const newPatientPhoneNumber = nextPatient.patient_phone_number;
    const nextFileToken = nextPatient.file_token;
    const newPrivacyLevel = nextPatient.privacy_level;
    const newDocumentTitle = nextPatient.document_title;
    const newDocumentCategory = nextPatient.document_category;

    const updates = {};

    // The standard owner EmailAttachment editor owns a frozen opening draft.
    // Legacy callers synchronize fields when the record itself changes.
    if (!this.sparseSaveEligible()) {
      const recordChanged = nextFileToken && nextFileToken !== currentFileToken;
      if (recordChanged) {
        if (newPatientName !== undefined && newPatientName !== null && newPatientName !== patientName) updates.patientName = newPatientName;
        if (newPatientFirstName !== undefined && newPatientFirstName !== null && newPatientFirstName !== patientFirstName) updates.patientFirstName = newPatientFirstName;
        if (newPatientMiddleName !== undefined && newPatientMiddleName !== null && newPatientMiddleName !== patientMiddleName) updates.patientMiddleName = newPatientMiddleName;
        if (newPatientLastName !== undefined && newPatientLastName !== null && newPatientLastName !== patientLastName) updates.patientLastName = newPatientLastName;
        if (newPatientPhoneNumber !== undefined && newPatientPhoneNumber !== null && newPatientPhoneNumber !== patientPhoneNumber) {
          updates.patientPhoneNumber = newPatientPhoneNumber;
        }
        if (newPrivacyLevel !== undefined && newPrivacyLevel !== null && newPrivacyLevel !== privacyLevel) updates.privacyLevel = newPrivacyLevel;
        updates.fileToken = nextFileToken;
      }
      if (newPatientDOB !== undefined && newPatientDOB !== null && newPatientDOB !== patientDOB) updates.patientDOB = newPatientDOB;
      if (newDocumentTitle !== undefined && newDocumentTitle !== null && newDocumentTitle !== documentTitle) updates.documentTitle = newDocumentTitle;
      if (newDocumentCategory !== undefined && newDocumentCategory !== null && newDocumentCategory !== documentCategory) updates.documentCategory = newDocumentCategory;
    }

    if (Object.keys(updates).length > 0) {
      this.setState(updates);
    }
  }

  componentWillUnmount() {
    this.editorActive = false;
  }

  validateFormFields = () => {
    if (!this.formRef.current) return;

    const { validateFields } = this.formRef.current;
    validateFields(['name', 'documentTitle']);
  };

  privacyOnlyMode = () => this.props.modalType.data.patient.record_update_mode === 'privacy_only';

  // True when RecordViewer opened this automatically because the user just
  // arrived from the Printer Driver, rather than from "Edit Record Info".
  printerUploadPrompt = () => printerUploadPromptEnabled(this.props.modalType.data);

  phoneNumberIsValid = (phoneNumber = this.state.patientPhoneNumber) => {
    const { patient } = this.props.modalType.data;
    const originalPhoneNumber = patient.patient_phone_number || '';
    const nextPhoneNumber = phoneNumber || '';

    if (!patient.can_edit_patient_info || nextPhoneNumber === originalPhoneNumber) {
      return true;
    }
    return !nextPhoneNumber || validPhoneNumber(nextPhoneNumber);
  };

  handlePrivacyKeyDown = async (event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    await this.update();
  };

  canDisplayPrivacyLevel = () => this.privacyPickerVisible(this.state.display);

  // What canDisplayPrivacyLevel asks, with the department_sharing answer passed
  // in: the flag only resolves after mount, so the code that reacts to it has to
  // be able to ask the question with the value it has just been handed rather
  // than the one still sitting in state.
  privacyPickerVisible = (display) => {
    const { modalType } = this.props;

    return modalType.data.patient.can_edit_privacy_level && (display || this.privacyOnlyMode());
  };

  receivedRecordLabelWorkflowEnabled = () => {
    if (this.privacyOnlyMode()) return false;

    const { modalType } = this.props;
    const { fromPage } = modalType.data;
    return window.location.pathname.includes('received') || fromPage === 'received';
  }

  dialogTitle = () => {
    const { title } = this.props.modalType.data;
    if (title) return title;
    // "Edit Record Info" reads as a correction; on a Printer Driver (or
    // Medsender iOS app) arrival the record has no patient info and an
    // unconfirmed privacy level yet, so this is the first pass over it, not an edit.
    if (this.printerUploadPrompt()) return printerUploadPromptTitle(this.props.modalType.data);

    return this.privacyOnlyMode() ? 'Change Privacy' : 'Edit Record Info';
  }

  determineVisibility = async () => {
    const [display, hideOnlyMePrivacyLevel] = await Promise.all([
      verifyFeatureAccess('department_sharing'),
      verifyFeatureAccess('hide_only_me_privacy_level'),
    ]);
    this.applyDepartmentSharingVisibility(display, hideOnlyMePrivacyLevel);
  }

  // Split from the flag read above so that what the flag unlocks is one
  // synchronous step. Resolving it is the first moment we know whether the
  // privacy picker will be on screen, which is the moment a Printer Driver
  // arrival may adopt the user's remembered level — see
  // rememberedPrivacyLevelUpdate.
  applyDepartmentSharingVisibility = (display, hideOnlyMePrivacyLevel = this.state.hideOnlyMePrivacyLevel) => {
    this.setState((state) => ({
      display,
      hideOnlyMePrivacyLevel,
      ...this.rememberedPrivacyLevelUpdate(state, display, hideOnlyMePrivacyLevel),
    }));
  };

  privacyLevelAvailable = (privacyLevel, hideOnlyMePrivacyLevel = this.state.hideOnlyMePrivacyLevel) => {
    if (privacyLevel === 'owner' && hideOnlyMePrivacyLevel) return false;
    return ['owner', 'department', 'organization'].includes(privacyLevel);
  };

  // The remembered level may only be adopted while the picker is on screen: both
  // save paths submit privacyLevel either way, so substituting it behind a
  // hidden picker would change the record's privacy to something the user never
  // saw. That cannot be decided in the `state` initializer — department_sharing
  // is still in flight there — so the modal opens on the record's own level and
  // this upgrades it once the flag lands.
  //
  // Only a level still sitting at the record's own value is substituted: the
  // picker is live from the first render, and a choice the user has already made
  // outranks a remembered default.
  rememberedPrivacyLevelUpdate = (state, display, hideOnlyMePrivacyLevel = this.state.hideOnlyMePrivacyLevel) => {
    const { patient } = this.props.modalType.data;
    if (!this.printerUploadPrompt()) return null;
    if (!this.privacyPickerVisible(display)) return null;
    if (state.privacyLevel !== patient.privacy_level) return null;

    // The printer stamps every upload with the organization default, so the
    // record's own level says nothing about what this user wants. Their
    // remembered choice is the better starting point; the record's level is the
    // fallback when they have never made one.
    const rememberedLevel = rememberedPrivacyLevel(patient.privacy_level);
    if (!this.privacyLevelAvailable(rememberedLevel, hideOnlyMePrivacyLevel)) return null;
    return { privacyLevel: rememberedLevel };
  };

  deselectLabel = (index) => {
    const { selectedLabels } = this.state;
    const currentlySelected = selectedLabels;
    currentlySelected.splice(index, 1);
    this.setState({
      selectedLabels: [...currentlySelected],
    });
  }

  checkLabelsChanged = () => {
    const { selectedLabels, initialLabels } = this.state;
    if (selectedLabels.length !== initialLabels.length) {
      return true;
    }
    for (let i = 0; i < selectedLabels.length; i++) {
      if (selectedLabels[i].id !== initialLabels[i].id) {
        return true;
      }
    }
    return false;
  }

  sparseSaveEligible = () => this.sparseSaveEnabled;

  snapshotDraft = () => ({
    ...this.state,
    selectedLabels: [...(this.state.selectedLabels || [])],
  });

  sparsePatientChanges = (draft) => {
    const changes = buildSparsePatientChanges(
      this.openingPatientBaseline || normalizePatientDraft(draft),
      draft,
    );
    if (draft.incorrectName) changes.is_patient_name_incorrect = true;
    if (draft.incorrectDOB) changes.is_patient_dob_incorrect = true;
    return changes;
  };

  activeSparsePatientUpdate = () => {
    const { sparsePatientUpdate } = this.props;
    return sparsePatientUpdate.fileToken === this.state.fileToken
      ? sparsePatientUpdate
      : IDLE_SPARSE_PATIENT_UPDATE;
  };

  activeEditorMatches = (fileToken) => (
    this.editorActive
    && this.props.modalType.data.patient.file_token === fileToken
  );

  notifyPatientSaved = () => toastr.success('You have successfully updated the record');

  submitSparseSave = async (draft) => {
    const {
      decrementReferralCount,
      editReceivedRecordLabels,
      hideModal,
      incrementReferralCount,
      modalType,
      reportSparseUpdateError,
      sparseEdit,
      updateSelectedRecordLabels,
    } = this.props;
    const {
      documentCategory,
      fileToken,
      selectedLabels,
      sendTokens,
    } = draft;
    const labelsChanged = this.checkLabelsChanged();
    const labelsEnabled = this.receivedRecordLabelWorkflowEnabled();
    const patientChanges = this.sparsePatientChanges(draft);
    if (Object.keys(patientChanges).length === 0 && !labelsChanged) return undefined;

    if (labelsEnabled && labelsChanged) {
      if (!this.activeEditorMatches(fileToken)) return undefined;
      const labelResult = await editReceivedRecordLabels(
        sendTokens || modalType.data.record,
        selectedLabels.map((label) => label.id),
        selectedLabels,
        modalType.data.receiverType,
      );
      if (!this.activeEditorMatches(fileToken)) return undefined;
      if (labelResult?.error) {
        reportSparseUpdateError(fileToken, labelResult);
        return labelResult;
      }
      updateSelectedRecordLabels(fileToken, selectedLabels);
      if (this.props.updatedLabel) message.success({ content: 'Labels changed' });
    }

    if (Object.keys(patientChanges).length > 0) {
      captureAnalytics('email_attachment_sparse_patient_save', {
        changed_field_count: Object.keys(patientChanges).length,
        labels_changed: labelsChanged,
        from_page: modalType.data.fromPage || '',
        receiver_type: 'email_attachment',
      });
      const result = await sparseEdit({
        fileToken,
        changes: patientChanges,
        fromPage: modalType.data.fromPage,
      });
      // A cancelled request comes back as { canceled: true } with no `error`
      // (see actions/helpers/crud.js), so success has to be asserted rather than
      // inferred from the absence of an error: nothing was saved, and treating
      // it as saved would toast, close the modal and remember a privacy level
      // for a write that never happened.
      if (!this.saveSucceeded(result) || !this.activeEditorMatches(fileToken)) return result;

      this.rememberPrinterUploadPrivacy(draft.privacyLevel);
      this.notifyPatientSaved();
      if (labelsEnabled) {
        const delta = referralCountDelta(
          modalType.data.patient.document_category,
          documentCategory,
        );
        if (delta === 'increment') await incrementReferralCount();
        if (delta === 'decrement') await decrementReferralCount();
      }
    }

    if (!this.activeEditorMatches(fileToken)) return undefined;
    hideModal();
    return undefined;
  };

  // Positive evidence that a save landed. crud resolves to the response on
  // success, to { ...payload, error: true } on failure, and to { canceled: true }
  // with no `error` when the request was cancelled — the last of which reads as
  // success to an `!error` test.
  saveSucceeded = (result) => Boolean(result) && !result.error && !result.canceled;

  performUpdate = async (event) => {
    event?.preventDefault?.();
    const draft = this.snapshotDraft();
    if (this.sparseSaveEligible()) {
      if (!this.phoneNumberIsValid(draft.patientPhoneNumber)) {
        if (this.formRef.current) {
          try {
            await this.formRef.current.validateFields(['patientPhoneNumber']);
          } catch {
            return undefined;
          }
        }
        return undefined;
      }
      if (draft.patientDOB && !isValidPatientDOB(draft.patientDOB)) {
        this.setState({ patientValid: 'Invalid DOB' });
        return undefined;
      }
      if (this.disableSubmitButton()) return undefined;
      return this.submitSparseSave(draft);
    }
    const {
      fileToken,
      patientDOB,
      patientName,
      patientFirstName,
      patientMiddleName,
      patientLastName,
      patientPhoneNumber,
      privacyLevel,
      selectedLabels,
      sendTokens,
      incorrectName,
      incorrectDOB,
      documentTitle,
      documentCategory,
      referralNote,
      referringProvider,
      referringProviderOffice,
    } = draft;
    const {
      editReceivedRecordLabels, mode, edit, hideModal, modalType, incrementReferralCount, decrementReferralCount,
    } = this.props;
    const { fromPage, patient } = modalType.data;
    const privacyOnly = patient.record_update_mode === 'privacy_only';
    if (!privacyOnly && !this.phoneNumberIsValid()) {
      if (this.formRef.current) {
        try {
          await this.formRef.current.validateFields(['patientPhoneNumber']);
        } catch {
          return;
        }
      }
      return;
    }
    const submitPatientUpdate = async (dob) => {
      const result = await edit(
        fileToken,
        dob,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        patientPhoneNumber,
        mode,
        privacyLevel,
        fromPage,
        incorrectDOB,
        incorrectName,
        documentTitle,
        documentCategory,
        referralNote,
        referringProvider,
        referringProviderOffice,
        privacyOnly,
      );
      if (this.saveSucceeded(result)) this.rememberPrinterUploadPrivacy(privacyLevel);
      return result;
    };
    if (privacyOnly) {
      if (this.disableSubmitButton()) return;

      const result = await submitPatientUpdate(null);
      if (!this.saveSucceeded(result)) return;
      hideModal();
      this.updateRowSelected();
      return;
    }

    const isPatientInfoIncorrect = (incorrectDOB || incorrectName);
    const selectedLabelIds = [];
    selectedLabels.map((label) => selectedLabelIds.push(label.id));
    if (this.checkPatientInfo(patientDOB, patientName, patientFirstName, patientMiddleName, patientLastName, patientPhoneNumber, privacyLevel, selectedLabels, documentTitle, documentCategory)) {
      const dob = formatDate(patientDOB);
      // only make request to update labels when looking at a received record
      if (this.receivedRecordLabelWorkflowEnabled()) {
        // If sending update from received inbox, check if we're updating the document category.
        const oldCategory = this.props.modalType.data.patient.document_category;

        // Check if we're changing the document category.
        if (oldCategory !== documentCategory) {
          if (documentCategory?.toLowerCase() === 'referral') {
            // If the new document category is referral, increment the new referral count.
            await incrementReferralCount();
          } else if (oldCategory?.toLowerCase() === 'referral') {
            // If the old document category was referral, decrement the new referral count.
            await decrementReferralCount();
          }
        }

        if (this.checkLabelsChanged()) {
          await editReceivedRecordLabels(sendTokens || modalType.data.record, selectedLabelIds, selectedLabels, modalType.data.receiverType);
        }
        const { updatedLabel } = this.props;
        if (updatedLabel) {
          message.success({
            content: 'Labels changed',
          });
        }
      }
      if (this.checkPatientInfo(patientDOB, patientName, patientFirstName, patientMiddleName, patientLastName, patientPhoneNumber, privacyLevel, selectedLabels, documentTitle, documentCategory)) {
        const result = await submitPatientUpdate(dob);
        if (!this.saveSucceeded(result)) return;
      }
      hideModal();
      this.updateRowSelected();

      // Open the send dialog after updating the patient data if 'showSendDialog'
      // was passed in as props. This will only happen if the user is attempting
      // to send a record without the patient name &/or dob.
      //
      // This is the only save path that can carry the hook: a modal with
      // showSendDialog in its data is never eligible for the sparse save (see
      // sparsePatientSaveEligible in ./emailAttachmentPatientSave).
      if ('showSendDialog' in this.props.modalType.data) {
        this.props.modalType.data.showSendDialog(true);
      }
      return;
    }
    if (!(patientFirstName && patientLastName) && !isPatientInfoIncorrect) return;
    if (!patientDOB && !isPatientInfoIncorrect) {
      const result = await submitPatientUpdate(null);
      if (!this.saveSucceeded(result)) return;
      hideModal();
      this.updateRowSelected();

      // Open the send dialog after updating the patient data if 'showSendDialog'
      // was passed in as props. This will only happen if the user is attempting
      // to send a record without the patient name &/or dob
      if ('showSendDialog' in modalType.data) {
        modalType.data.showSendDialog(true);
      }
      return;
    }
    if (isPatientInfoIncorrect) {
      const result = await submitPatientUpdate(patientDOB);
      if (!this.saveSucceeded(result)) return undefined;
      hideModal();

      // Marking the extracted info incorrect is still a successful save, so
      // the auto-send hand-off must survive it like the other legacy paths.
      if ('showSendDialog' in modalType.data) {
        modalType.data.showSendDialog(true);
      }
      return undefined;
    }
    const validDate = validDOB(patientDOB);
    if (!validDate && !isPatientInfoIncorrect) {
      this.setState({ patientValid: 'Invalid DOB' });
      return;
    }
    toastr.warning('No new changes. Click cancel to close.');
  }

  renderSelectLabel = () => {
    const { isLoading, hideModal } = this.props;
    const { labels, selectedLabels, searchLoading } = this.state;
    const { onSearch } = this;
    if (isLoading) {
      return (<span> Updating labels, Please wait... </span>);
    }
    const maxAdditionalLabels = 5 - selectedLabels.length;
    const allowedAdditionalLabels = maxAdditionalLabels > 0;
    const noLabelsSelected = selectedLabels.length === 0;
    return (
      <>
        <div className="edit-record-info__label-row">
          <div>
            <span className="edit-record-info__label-title">Current labels</span>
            <span className={allowedAdditionalLabels ? 'edit-record-info__label-count' : 'edit-record-info__label-count edit-record-info__label-count--limit'}>
              {allowedAdditionalLabels
                ? `${maxAdditionalLabels} remaining`
                : 'Remove a label to add another'}
            </span>
          </div>
          <Button
            type="link"
            size="small"
            onClick={() => this.setState({ selectedLabels: [] })}
            disabled={noLabelsSelected}
          >
            Clear all
          </Button>
        </div>
        <div className="edit-record-info__selected-labels">
          {noLabelsSelected
            ? <span className="edit-record-info__empty-labels">No labels selected</span>
            : (
              selectedLabels.map((label, index) => (
                <Tag
                  color={label.color}
                  closable={true}
                  key={`${label.name}${index}`}
                  onClose={() => this.deselectLabel(index)}
                  style={{ color: label.is_dark_text ? 'black' : 'white' }}
                >
                  {label.name}
                </Tag>
              ))
            )}
        </div>
        <div className="edit-record-info__label-tools">
          <Search
            placeholder="Search labels"
            allowClear
            onSearch={onSearch}
            onChange={(e) => this.setState({ searchValue: e.target.value })}
            disabled={!allowedAdditionalLabels}
            loading={searchLoading}
            onKeyUp={(e) => {
              this.setState({ searchLoading: true });
              clearTimeout(typingTimer);
              const query = e.target.value;
              typingTimer = setTimeout(() => {
                this.onSearch(query);
              }, 1000);
            }}
          />
          <span>
            Need another?
            <Link to="/app/user?tab=manage_labels" onClick={hideModal}> Manage labels</Link>
          </span>
        </div>
        <div className="edit-record-info__available-labels">
          {labels.map((label) => {
            const labelIsSelected = selectedLabels.find(selectedLabel => selectedLabel.name === label.name);
            const recordAlreadyHasLabel = selectedLabels.find(selectedRecordLabel => selectedRecordLabel.name === label.name);
            const labelTextColor = label.is_dark_text ? 'black' : 'white';
            return (
              <Tag
                key={label.id || label.name}
                onClick={() => !recordAlreadyHasLabel && allowedAdditionalLabels && this.selectLabel(label)}
                color={recordAlreadyHasLabel ? 'white' : label.color}
                style={{
                  color: recordAlreadyHasLabel ? label.color : labelTextColor,
                  cursor: allowedAdditionalLabels ? 'pointer' : 'not-allowed',
                  border: recordAlreadyHasLabel ? `1px dotted ${label.color}` : (labelIsSelected && '2px solid black'),
                }}
              >
                {label.name}
              </Tag>
            );
          })}
        </div>
      </>
    );
  }

  renderPatientInfoIncorrectCheckBox = (isPatientInfoIncorrect) => {
    return (
      <>
        <Checkbox
          checked={isPatientInfoIncorrect}
          onChange={() => this.setState(prevState => ({
            isPatientInfoIncorrect: !prevState.isPatientInfoIncorrect,
            incorrectName: !prevState.isPatientInfoIncorrect ? prevState.incorrectName : false,
            incorrectDOB: !prevState.isPatientInfoIncorrect ? prevState.incorrectDOB : false,
          }))}
          disabled={false}
          style={{ marginBottom: '10px', display: 'inline' }}
        >
          Is patient Information wrongly extracted?
        </Checkbox>
        <Tooltip
          overlayStyle={{ marginLeft: '20px' }}
          title="Reporting errors with our AI Extraction will help us train our model and become more accurate in the future"
        >
          <QuestionCircleOutlined />
        </Tooltip>
        <br />
        <br />
      </>
    );
  }

  renderPatientInfoIncorrectCheckBoxes = () => {
    const { incorrectDOB, incorrectName } = this.state;
    return (
      <Form.Item
        label="Which information was wrongly extracted?"
        style={{ marginTop: '10px' }}
      >
        <Checkbox.Group>
          <Row>
            <Checkbox
              checked={incorrectName}
              value="incorrect_name"
              style={{ lineHeight: '32px' }}
              disabled={false}
              onChange={() => this.setState(prevState => ({
                incorrectName: !prevState.incorrectName,
              }))}
            >
              Patient Name
            </Checkbox>
          </Row>
          <Row>
            <Checkbox
              checked={incorrectDOB}
              value="incorrect_dob"
              style={{ lineHeight: '32px' }}
              disabled={false}
              onChange={() => this.setState(prevState => ({
                incorrectDOB: !prevState.incorrectDOB,
              }))}
            >
              Patient Date of Birth
            </Checkbox>
          </Row>
        </Checkbox.Group>
      </Form.Item>
    );
  }

  handleNameFieldChange = (fieldName) => (event) => {
    this.setState({
      [fieldName]: event.target.value,
      patientNameChanged: true,
    });
    this.validateFormFields();
    // Trigger validation on all name fields when any name field changes
    setTimeout(() => {
      this.formRef.current.validateFields(['patientFirstName', 'patientLastName', 'documentTitle']);
    }, 0);
  };

  handleNoteChange = (event) => {
    this.setState({
      referralNote: event.target.value,
    });
    this.validateFormFields();
  }

  handleReferringProviderChange = (event) => {
    this.setState({
      referringProvider: event.target.value,
    });
    this.validateFormFields();
  }

  handleReferringProviderOfficeChange = (event) => {
    this.setState({
      referringProviderOffice: event.target.value,
    });
    this.validateFormFields();
  }

  handleDocumentTitleChange = (event) => {
    this.setState({
      documentTitle: event.target.value,
    });
    this.validateFormFields();

    // Trigger validation on all name fields when any document title changes
    setTimeout(() => {
      this.formRef.current.validateFields(['patientFirstName', 'patientLastName', 'documentTitle']);
    }, 0);
  }

  /**
   * @param {String} dateString format MM/DD/YYYY
   */
  handleDateChange = (_, dateString) => {
    this.setState({ patientDOB: dateString });
  }

  handlePhoneChange = (event) => {
    this.setState({ patientPhoneNumber: event.target.value });
  }

  handleDropdownChange = (value) => {
    return this.setState({
      incorrectName: value.includes('name'),
      incorrectDOB: value.includes('dob'),
    });
  }

  // select label functions
  selectLabel = (label) => {
    const { selectedLabels } = this.state;
    const currentlySelected = [...selectedLabels];
    const indexOfSelectedLabel = currentlySelected.findIndex(selectedLabel => selectedLabel.name === label.name);
    const labelAlreadySelected = currentlySelected.find(selectedLabel => selectedLabel.name === label.name);
    if (!labelAlreadySelected) {
      this.setState({
        selectedLabels: [...currentlySelected, label],
      });
    } else {
      currentlySelected.splice(indexOfSelectedLabel, 1);
      this.setState({
        selectedLabels: currentlySelected,
      });
    }
  }

  onSearch = async (value) => {
    const { getLabelsByQuery } = this.props;
    const params = {
      page: 1,
      page_size: 20,
      contains: value,
    };
    const query = stringify(params, stringifyOptions);
    await getLabelsByQuery(query);
    const { data } = this.props;
    this.setState({
      labels: data,
      searchLoading: false,
    });
  }

  searchKeyUp = (event) => {
    this.setState({ searchLoading: true });
    // Maintain a 1 second timer before sending request
    // If any new key is pressed during the course of that
    // timer, reset the timer
    clearTimeout(typingTimer);
    const query = event.target.value;
    typingTimer = setTimeout(() => {
      this.searchQuery(query);
    }, 1000);
  }

  // Unchecking the box takes effect there and then, rather than waiting for a
  // save that the user may abandon or the form may reject: the visible state of
  // the checkbox and the stored preference should not be able to disagree.
  // Re-checking writes nothing yet — the save does that, with the level the
  // record ended up on.
  togglePrinterUploadPrivacyMemory = (checked) => {
    this.setState({ useSelectedPrivacyLevel: checked });
    if (!checked) forgetPrivacyLevel();
    // Only whether the box is checked — never the level or anything about the
    // record — so the event carries no PHI.
    captureAnalytics('printer_upload_privacy_memory_toggled', { checked });
  };

  dismissModal = () => {
    const { hideModal, modalType } = this.props;
    hideModal();
    if (this.printerUploadPrompt() && 'showSendDialog' in modalType.data) {
      modalType.data.showSendDialog(true);
    }
  };

  // The arrival prompt's "use for all future uploads" checkbox. Called from
  // both save paths — the sparse email-attachment save and the legacy one —
  // because which one runs depends on receiverType and on whether the Gibraltar
  // send preference attached showSendDialog, and neither should decide whether
  // the user's stated preference survives. Always after a successful submit:
  // remembering a level that never reached the record would seed every later
  // upload from a failure.
  rememberPrinterUploadPrivacy(privacyLevel) {
    if (!this.printerUploadPrompt()) return;
    // The same visibility the remembered level is read behind, for the same
    // reason: with the picker hidden the checkbox is hidden with it, so its
    // checked-by-default state is not something the user said. Writing it would
    // hand whatever level the record happens to carry to every future upload on
    // the strength of a control that was never on screen. By save time the
    // department_sharing flag has resolved, so this can read state directly.
    if (!this.canDisplayPrivacyLevel()) return;
    if (!this.privacyLevelAvailable(privacyLevel)) return;

    rememberPrivacyLevel(privacyLevel, this.state.useSelectedPrivacyLevel);
  }

  updateRowSelected() {
    // eslint-disable-next-line object-curly-newline
    const { fileToken, patientDOB, patientFirstName, patientMiddleName, patientLastName, patientPhoneNumber, privacyLevel, selectedLabels, documentTitle, documentCategory } = this.state;
    if (this.privacyOnlyMode()) {
      this.props.rowSelected([{
        file_token: fileToken,
        index: this.props.modalType.data.patient.index,
        privacy_level: privacyLevel,
      }]);
      return;
    }

    const fullPatientName = [patientFirstName, patientMiddleName, patientLastName]
      .filter(name => name && name.trim())
      .join(' ').trim();
    const selectedRow = {
      patient_name: fullPatientName,
      patient_dob: patientDOB,
      patient_phone_number: patientPhoneNumber,
      file_token: fileToken,
      index: this.props.modalType.data.patient.index,
      privacy_level: privacyLevel,
      labels: selectedLabels,
      document_title: documentTitle,
      document_category: documentCategory,
    };
    this.props.rowSelected([selectedRow]);
  }

  checkPatientInfo(patientDOB, patientName, patientFirstName, patientMiddleName, patientLastName, patientPhoneNumber, privacyLevel, selectedLabels, documentTitle, documentCategory) {
    const {
      patient:
        {
          patient_dob: oldPatientDOB,
          patient_name: oldPatientName,
          patient_first_name: oldPatientFirstName,
          patient_middle_name: oldPatientMiddleName,
          patient_last_name: oldPatientLastName,
          patient_phone_number: oldPatientPhoneNumber,
          privacy_level: oldPrivacyLevel,
          document_title: oldDocumentTitle,
          document_category: oldDocumentCategory,
          labels,
        },
    } = this.props.modalType.data;
    const { incorrectName, incorrectDOB } = this.state;
    return (
      oldPatientDOB !== patientDOB
      || oldPatientName !== patientName
      || oldPatientFirstName !== patientFirstName
      || oldPatientMiddleName !== patientMiddleName
      || oldPatientLastName !== patientLastName
      || (oldPatientPhoneNumber || '') !== (patientPhoneNumber || '')
      || oldPrivacyLevel !== privacyLevel
      || oldDocumentTitle !== documentTitle
      || oldDocumentCategory !== documentCategory
      || labels !== selectedLabels
      || incorrectName
      || incorrectDOB
    );
  }

  disableSubmitButton() {
    const {
      patientName,
      patientDOB,
      privacyLevel,
      incorrectName,
      incorrectDOB,
      documentTitle,
      documentCategory,
      referralNote,
      referringProvider,
      referringProviderOffice,
      patientFirstName,
      patientLastName,
      patientNameChanged,
      patientMiddleName,
      patientPhoneNumber,
    } = this.state;
    const { modalType: { data, data: { patient } } } = this.props;
    if (this.privacyOnlyMode()) {
      return !privacyLevel || patient.privacy_level === privacyLevel;
    }
    if (!this.phoneNumberIsValid()) {
      return true;
    }
    // Name/title validation for enabling submit:
    // - If any name field is provided, require BOTH first and last
    // - If no name fields are provided, allow submit only if document title is present
    const hasFirst = !!(patientFirstName && patientFirstName.trim());
    const hasMiddle = !!(patientMiddleName && patientMiddleName.trim());
    const hasLast = !!(patientLastName && patientLastName.trim());
    const hasAnyName = hasFirst || hasMiddle || hasLast;
    const hasBothFirstAndLast = hasFirst && hasLast;

    if (hasAnyName && !hasBothFirstAndLast) {
      return true;
    }
    if (!hasAnyName && !(documentTitle && documentTitle.trim())) {
      return true;
    }
    const labelsChanged = this.checkLabelsChanged();
    if (this.sparseSaveEligible()) {
      return Object.keys(this.sparsePatientChanges(this.state)).length === 0 && !labelsChanged;
    }
    if (data.buttonText && data.buttonText === 'Continue') {
      return (
        patient.patient_name === patientName
        && !incorrectName
        && !documentTitle
      );
    }
    return (
      (!patientNameChanged
      && patient.patient_dob === patientDOB
      && (patient.patient_phone_number || '') === (patientPhoneNumber || '')
      && patient.privacy_level === privacyLevel
      && patient.document_title === documentTitle
      && patient.document_category === documentCategory
      && !incorrectName
      && !incorrectDOB
      && !labelsChanged
      && patient.referral_note === referralNote
      && patient.referring_provider === referringProvider
      && patient.provider_office === referringProviderOffice)
      || (!(patientFirstName && patientLastName) && !documentTitle)
    );
  }

  render() {
    // eslint-disable-next-line object-curly-newline
    const { patientName, patientFirstName, patientMiddleName, patientLastName, patientDOB, patientPhoneNumber, privacyLevel, useSelectedPrivacyLevel, isPatientInfoIncorrect, autoLabelAccess, sendTokens, documentTitle, documentCategory, documentCategories, referralNote, referringProvider, referringProviderOffice} = this.state;
    const {
      isLoading,
      modalType,
      sendToken,
    } = this.props;
    const {
      data: {
        patient: {
          has_wrong_extraction_been_reported: hasWrongExtractionBeenReported,
          ai_status: aiStatus,
          patient_info_edited: isPatientInfoEdited,
          failed_column: failedColumn,
        },
      },
    } = modalType;
    const { buttonText } = modalType.data;
    const dialogTitle = this.dialogTitle();
    const dialogButtonText = buttonText || 'Save changes';
    const bodyStyle = { padding: 0 };
    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const singleColumnFailed = aiStatus === 'failed' && failedColumn !== 'both';
    const isAiExtractionCorrect = aiStatus === 'successful' || singleColumnFailed;
    const sparsePatientUpdate = this.activeSparsePatientUpdate();
    return (
      <ModalWrapper
        hideModal={this.dismissModal}
        bodyStyle={bodyStyle}
        action={this.update}
        actionName={dialogButtonText}
        dismiss="Cancel"
        modalTitle={dialogTitle}
        isLoading={this.sparseSaveEligible() ? sparsePatientUpdate.isLoading : isLoading}
        disabled={this.disableSubmitButton()}
        isDrawer={true}
        width={680}
        wrapClassName="edit-record-info-drawer"
        customContentStyle={{}}
      >
        <div className="edit-record-info">
          {sparsePatientUpdate.errorMessage && (
            <Alert
              role="alert"
              style={{ marginBottom: 10 }}
              message={sparsePatientUpdate.errorMessage}
              type="error"
              showIcon
            />
          )}
          {!this.privacyOnlyMode() && !autoLabelAccess && (
            <Alert
              style={{ marginBottom: 10, cursor: 'pointer' }}
              message={(
                <div>
                  <span style={{ fontWeight: 500 }}>Tired of labeling your documents?</span>
                  &nbsp;Schedule a 15 minute call with us to learn about our new AI-powered auto-labeling feature.
                </div>
              )}
              onClick={this.openWebinarLink}
              type="info"
              showIcon
            />
          )}
          {modalType.data.patient.can_edit_patient_info && (
            <Form
              className="edit-record-info__form"
              layout="vertical"
              onFinish={this.update}
              ref={this.formRef}
              initialValues={{
                name: patientName,
                documentTitle,
                note: referralNote,
                provider: referringProvider,
                office: referringProviderOffice,
                patientFirstName,
                patientLastName,
                patientMiddleName,
                patientPhoneNumber,
              }}
            >
              <section className="edit-record-info__section">
                <div className="edit-record-info__section-heading">
                  <h3>Patient details</h3>
                </div>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={8}>
                    <Form.Item
                      hasFeedback
                      label="First Name"
                      name="patientFirstName"
                      rules={[
                        ({ getFieldValue }) => ({
                          validator() {
                            const docTitle = getFieldValue('documentTitle');
                            const firstName = getFieldValue('patientFirstName');
                            const middleName = getFieldValue('patientMiddleName');
                            const lastName = getFieldValue('patientLastName');

                            const hasFirst = firstName && firstName.trim();
                            const hasMiddle = middleName && middleName.trim();
                            const hasLast = lastName && lastName.trim();
                            // If middle is present, require both first and last
                            if (hasMiddle && !(hasFirst && hasLast)) {
                              return Promise.reject(new Error('Both first and last name are required'));
                            }
                            // Check if we have a valid name (first AND last) OR documentTitle
                            const hasValidName = hasFirst && hasLast;
                            const hasDocumentTitle = docTitle && docTitle.trim();

                            if (hasValidName || hasDocumentTitle) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Both Patient Name and Document Title cannot be blank'));
                          },
                        }),
                      ]}
                    >
                      <Input
                        placeholder="First Name"
                        onChange={this.handleNameFieldChange('patientFirstName')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      label="Middle Name"
                      name="patientMiddleName"
                    >
                      <Input
                        placeholder="Middle Name"
                        onChange={this.handleNameFieldChange('patientMiddleName')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item
                      hasFeedback
                      label="Last Name"
                      name="patientLastName"
                      rules={[
                        ({ getFieldValue }) => ({
                          validator() {
                            const docTitle = getFieldValue('documentTitle');
                            const firstName = getFieldValue('patientFirstName');
                            const middleName = getFieldValue('patientMiddleName');
                            const lastName = getFieldValue('patientLastName');

                            const hasFirst = firstName && firstName.trim();
                            const hasMiddle = middleName && middleName.trim();
                            const hasLast = lastName && lastName.trim();
                            // If middle is present, require both first and last
                            if (hasMiddle && !(hasFirst && hasLast)) {
                              return Promise.reject(new Error('Both first and last name are required'));
                            }
                            // Check if we have a valid name (first AND last) OR documentTitle
                            const hasValidName = hasFirst && hasLast;
                            const hasDocumentTitle = docTitle && docTitle.trim();

                            if (hasValidName || hasDocumentTitle) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Both Patient Name and Document Title cannot be blank'));
                          },
                        }),
                      ]}
                    >
                      <Input
                        placeholder="Last Name"
                        onChange={this.handleNameFieldChange('patientLastName')}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Date of birth"
                      extra="MM/DD/YYYY"
                    >
                      <DatePicker
                        onChange={this.handleDateChange}
                        name="patientDOB"
                        format={dateFormat}
                        defaultValue={patientDOB && moment(patientDOB, dateFormat)}
                        value={patientDOB && moment(patientDOB, dateFormat)}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Phone number"
                      name="patientPhoneNumber"
                      rules={[
                        {
                          validator: (_rule, value) => (
                            this.phoneNumberIsValid(value)
                              ? Promise.resolve()
                              : Promise.reject(new Error('Enter a valid 10-digit phone number'))
                          ),
                        },
                      ]}
                    >
                      <Input
                        placeholder="(555) 555-5555"
                        value={patientPhoneNumber}
                        onChange={this.handlePhoneChange}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {(isAiExtractionCorrect && !hasWrongExtractionBeenReported && !isPatientInfoEdited) && this.renderPatientInfoIncorrectCheckBox(isPatientInfoIncorrect)}
                {isPatientInfoIncorrect && this.renderPatientInfoIncorrectCheckBoxes()}
              </section>

              <section className="edit-record-info__section">
                <div className="edit-record-info__section-heading">
                  <h3>Document details</h3>
                </div>
                <Row gutter={[16, 0]}>
                  <Col xs={24} md={14}>
                    <Form.Item
                      hasFeedback
                      label="Document title"
                      name="documentTitle"
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const firstName = getFieldValue('patientFirstName');
                            const middleName = getFieldValue('patientMiddleName');
                            const lastName = getFieldValue('patientLastName');
                            const hasFirst = firstName && firstName.trim();
                            const hasMiddle = middleName && middleName.trim();
                            const hasLast = lastName && lastName.trim();
                            // If middle is present, require both first and last
                            if (hasMiddle && !(hasFirst && hasLast)) {
                              return Promise.reject(new Error('Both first and last name are required'));
                            }
                            const hasValidName = hasFirst && hasLast;
                            if ((value && value.trim()) || hasValidName) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Both Patient Name and Document Title cannot be blank'));
                          },
                        }),
                      ]}
                    >
                      <Input
                        placeholder="Enter a document title"
                        onChange={this.handleDocumentTitleChange}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={10}>
                    <Form.Item label="Document category">
                      <Select
                        className="edit-record-info__category"
                        placeholder="Select a category"
                        allowClear
                        showSearch
                        onChange={(event) => this.setState({ documentCategory: event })}
                        value={documentCategory}
                      >
                        {dedupeDocumentCategories(documentCategories)
                          .map((emrCategory) => (
                            <Option
                              key={emrCategory.id}
                              value={emrCategory.emr_category}
                            >
                              {emrCategory.emr_category}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </section>
              {(documentCategory === 'Referral')
              && (
                <section className="edit-record-info__section">
                  <div className="edit-record-info__section-heading">
                    <h3>Referral details</h3>
                  </div>
                  <Form.Item
                    label="Note"
                    name="note"
                  >
                    <Input.TextArea
                      placeholder="Additional Note"
                      onChange={this.handleNoteChange}
                    />
                  </Form.Item>
                  <Row gutter={[16, 0]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Referring provider"
                        name="provider"
                      >
                        <Input
                          placeholder="Provider name"
                          onChange={this.handleReferringProviderChange}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Referring provider office"
                        name="office"
                      >
                        <Input
                          placeholder="Office name"
                          onChange={this.handleReferringProviderOfficeChange}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </section>
              )}
            </Form>
          )}
          {this.canDisplayPrivacyLevel() && (
            <Form
              className="edit-record-info__form"
              layout="vertical"
              onKeyDown={!modalType.data.patient.can_edit_patient_info ? this.handlePrivacyKeyDown : undefined}
            >
              <section className="edit-record-info__section">
                <div className="edit-record-info__section-heading">
                  <h3>Privacy</h3>
                </div>
                <Form.Item>
                  <div className="edit-record-info__privacy-options">
                    <PrivacyLevel
                      valueSelected={privacyLevel}
                      display={true}
                      handleChange={(selectedPrivacyLevel) => {
                        this.setState({ privacyLevel: selectedPrivacyLevel });
                      }}
                    />
                  </div>
                  {/* Same wording and same default as the web Upload Record
                      modal, so the printer path offers the same bargain: pick
                      once, stop being asked. Only shown on a Printer Driver
                      arrival — offering to change a default while editing an
                      unrelated received fax would be wrong. */}
                  {this.printerUploadPrompt() && (
                    <Checkbox
                      onChange={(event) => this.togglePrinterUploadPrivacyMemory(event.target.checked)}
                      checked={useSelectedPrivacyLevel}
                    >
                      Use selected privacy level for all future uploads.
                    </Checkbox>
                  )}
                </Form.Item>
              </section>
            </Form>
          )}
          {this.receivedRecordLabelWorkflowEnabled() && (sendToken || sendTokens) && (
            <section className="edit-record-info__section edit-record-info__labels">
              <div className="edit-record-info__section-heading">
                <h3>Labels</h3>
              </div>
              {this.renderSelectLabel()}
            </section>
          )}
        </div>
      </ModalWrapper>
    );
  }
}

export default connect((state) => ({
  documentCategories: state.auth,
  allDocumentCategories: state.userProfile.allDocumentCategories,
  errorMessage: state.records.errorMessage,
}))(EditPatient);
